// Copy (i18n text) layer — resolves the VISIBLE STRINGS of the navbar/footer for
// a locale, independent of link resolution (resolve-href.js handles URLs).
//
// Why this exists: copy and links are fused in the seed data — an item is
// `{ key, title, link }` where `title` is the English source string. To render
// another locale we look up a per-locale COPY BUNDLE (a flat map of
// `global.json` dot-path -> translated string) by the item's `key`, falling
// back to the inline English `title` when no bundle/key is present. So English
// always renders with zero locale data, and a missing key degrades to English
// rather than breaking.
//
// KEY CONVENTION (decision #2 in docs/PLAN.md — "reuse global.json key paths"):
// keys are the EXACT dot-paths from the marketing site's translations/en/global.json,
// so the generator (scripts/gen-locale-bundles.mjs) needs no mapping table — it
// reads global.json, flattens it, and pulls each key the component declares.
//   - menu label / column heading (leaf string): the `key` itself
//   - item title:       `${item.key}.title`
//   - item description: `${item.key}.description`
// The suffix rule lives HERE (itemCopyKeys) and is used by BOTH the components
// (at render) and collectKeys (for the generator + drift tests), so the two can
// never diverge.

import { LOCALE_BUNDLES } from './locales/index.js';

/**
 * The copy bundle for a locale, or null when none is bundled (→ English source).
 * @param {string} locale
 * @returns {Record<string,string>|null}
 */
export function getCopy(locale) {
  if (!locale || locale === 'en') return null; // English is the inline source
  return LOCALE_BUNDLES[locale] ?? null;
}

/**
 * Resolve one key to its localized string, falling back to `fallback` (the
 * inline English source) when the bundle or key is absent.
 * @param {string} key
 * @param {string} fallback
 * @param {Record<string,string>|null} [copyMap]
 * @returns {string}
 */
export function resolveCopy(key, fallback, copyMap) {
  if (!key || !copyMap) return fallback;
  const value = copyMap[key];
  return value == null || value === '' ? fallback : value;
}

/**
 * The copy keys for a data item.
 *
 * `key` is the FULL global.json key for the item's title — not a stem we append
 * a suffix to. An earlier version derived `${key}.title`, but the live
 * global.json has no single convention: items carrying a description are
 * `…specHub.title` / `…specHub.description`, while plain links are bare
 * (`…platform.whatsNew`). A derived suffix cannot express both, so items store
 * the real key directly and set `descriptionKey` when they have a description.
 *
 * @param {{key?: string, descriptionKey?: string, title?: string, description?: string}} item
 */
export function itemCopyKeys(item) {
  if (!item?.key) return { title: null, description: null };
  // Where global.json carries a description it sits beside the title under the
  // same stem (…specHub.title / …specHub.description), so the description key
  // is derivable. Bare title keys (plain links) never have one. `descriptionKey`
  // overrides for any item that breaks the pattern.
  const derived = item.key.endsWith('.title') ? item.key.replace(/\.title$/, '.description') : null;
  return {
    title: item.key,
    description: item.description != null ? (item.descriptionKey ?? derived) : null,
  };
}

/**
 * Resolve an item's visible copy (title + optional description) for a bundle.
 * Components call this; it keeps render logic and key derivation in one place.
 * @param {object} item
 * @param {Record<string,string>|null} [copyMap]
 * @returns {{title: string, description: string|undefined}}
 */
export function resolveItemCopy(item, copyMap) {
  const keys = itemCopyKeys(item);
  return {
    title: resolveCopy(keys.title, item.title, copyMap),
    description:
      item.description != null
        ? resolveCopy(keys.description, item.description, copyMap)
        : undefined,
  };
}

/**
 * Walk the navbar + footer data and return EVERY copy key the component needs,
 * paired with its English source string. This is the SINGLE SOURCE OF TRUTH the
 * generator fills from global.json and the drift test asserts full coverage of.
 * @param {object} opts
 * @param {Array} opts.navData
 * @param {Array} opts.footerColumns
 * @param {object} [opts.footerBottom]
 * @returns {Array<{key: string, en: string}>}
 */
/**
 * Keys used directly in a component's render() rather than living on a nav or
 * footer data node. collectKeys walks the DATA, so without this list these were
 * declared nowhere and the generator never pulled their translations — which is
 * why the logo's accessible name rendered English on /jp/ even though
 * nav.homeLabel has existed in global.json all along.
 */
export const UI_KEYS = [
  { key: 'nav.homeLabel', en: 'Home' },
  // The hamburger's accessible name. Its only content is an icon, so without
  // this the button announces as "button" on a phone — where the drawer is the
  // ONLY way into the menu. Same key the marketing site's own toggle uses.
  { key: 'nav.toggleMenu', en: 'Toggle navigation menu' },
  { key: 'nav.contactSales', en: 'Contact Sales' },
  { key: 'nav.signIn', en: 'Sign In' },
  { key: 'nav.signUp', en: 'Sign Up for Free' },
  { key: 'nav.launchPostman', en: 'Launch Postman' },
  // Language switcher. Rendered from string literals in postman-footer.js
  // rather than from footerColumns, so the walk below cannot see them and they
  // have to be declared here or the JA bundle ships without them — which is
  // exactly what happened: the switcher rendered an English "Languages"
  // heading under a JA footer until these two rows existed.
  { key: 'footer.section.languages', en: 'Languages' },
  { key: 'lang.selectLanguage', en: 'Language' },
];

export function collectKeys({ navData = [], footerColumns = [], footerBottom } = {}) {
  const out = [...UI_KEYS];
  const push = (key, en) => {
    if (key && en != null) out.push({ key, en });
  };

  const pushItem = (item) => {
    const keys = itemCopyKeys(item);
    push(keys.title, item.title);
    if (keys.description) push(keys.description, item.description);
  };

  for (const menu of navData) {
    push(menu.key, menu.title); // top-level menu label
    // The brands sidebar heading ("More from Postman") is a data node like any
    // label, not a UI_KEYS literal, so it travels with the menu it belongs to.
    push(menu.brandsHeading?.key, menu.brandsHeading?.title);
    for (const col of menu.columns ?? []) {
      push(col.key, col.title); // column heading (may be '' — skipped by push)
      for (const item of col.items ?? []) pushItem(item);
    }
    for (const item of menu.featured ?? []) pushItem(item);
    for (const item of menu.brands ?? []) pushItem(item);
  }

  for (const col of footerColumns) {
    push(col.key, col.title); // footer column heading
    for (const item of col.items ?? []) pushItem(item);
  }

  // Footer bottom-bar labels (download, privacy policy) are localizable items.
  if (footerBottom) {
    for (const item of [footerBottom.download, footerBottom.privacyPolicy]) {
      if (item?.key) pushItem(item);
    }
  }

  // De-dupe (a key should map to one English string; keep the first).
  const seen = new Set();
  return out.filter(({ key }) => (seen.has(key) ? false : (seen.add(key), true)));
}
